import React from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography } from '@material-ui/core';
import { PREINFORME_COLORS } from '../../constants/preinformeColors';
import preinformeStyles from './preinformeStyles';

/**
 * Tarjeta de valor sin barra de rangos, para los campos planos del
 * preinforme que no tienen métrica en el payload. Con la prop `atenuada`
 * (indicadores de payload.ausentes) muestra el valor "Sin dato" en lugar
 * del valor recibido.
 *
 * Props opcionales (feature 20, tarjetas de mediciones; ignoradas con
 * `atenuada`): `clasificacion` (texto bajo el valor), `filas` (lista de
 * { clave, etiqueta, valor, unidad, clasificacion }) y `fecha` (texto ya
 * formateado, se pinta como "Fecha: <fecha>"). Sin ellas el DOM no cambia.
 */
const ValorCard = ({ etiqueta, valor, unidad, atenuada, clasificacion, filas, fecha }) => {
    const classes = preinformeStyles();
    const borderColor = atenuada ? PREINFORME_COLORS.severidad.neutro : PREINFORME_COLORS.blue;
    const sinValorPrincipal = !atenuada && valor === null && filas.length > 0;
    const mostrarClasificacion = !atenuada && typeof clasificacion === 'string' && clasificacion !== '';
    const mostrarFecha = !atenuada && typeof fecha === 'string' && fecha !== '';

    return (
        <Paper
            className={`${classes.card} ${atenuada ? classes.cardAtenuada : ''}`}
            style={{ borderTopColor: borderColor }}
        >
            <div className={classes.cardHeaderRow}>
                <Typography className={classes.cardTitulo} component="span">
                    {etiqueta}
                </Typography>
            </div>

            {!sinValorPrincipal && (
                <div className={classes.valueRow}>
                    <Typography className={classes.valueNumber} component="span">
                        {atenuada ? 'Sin dato' : valor}
                    </Typography>
                    {!atenuada && unidad && (
                        <Typography className={classes.valueUnit} component="span">
                            {unidad}
                        </Typography>
                    )}
                </div>
            )}

            {mostrarClasificacion && (
                <Typography className={classes.cardClasificacion} component="span">
                    {clasificacion}
                </Typography>
            )}

            {!atenuada && filas.length > 0 && (
                <div className={classes.filasList}>
                    {filas.map((fila) => (
                        <div className={classes.filaRow} key={fila.clave}>
                            <Typography className={classes.filaEtiqueta} component="span">
                                {fila.etiqueta}
                            </Typography>
                            <Typography className={classes.filaValor} component="span">
                                {fila.unidad ? `${fila.valor} ${fila.unidad}` : fila.valor}
                            </Typography>
                            {typeof fila.clasificacion === 'string' && fila.clasificacion !== '' && (
                                <Typography className={classes.filaClasificacion} component="span">
                                    {fila.clasificacion}
                                </Typography>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {mostrarFecha && (
                <Typography variant="caption" className={classes.referencia}>
                    {`Fecha: ${fecha}`}
                </Typography>
            )}
        </Paper>
    );
};

ValorCard.propTypes = {
    etiqueta: PropTypes.string.isRequired,
    valor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unidad: PropTypes.string,
    atenuada: PropTypes.bool,
    clasificacion: PropTypes.string,
    filas: PropTypes.arrayOf(
        PropTypes.shape({
            clave: PropTypes.string.isRequired,
            etiqueta: PropTypes.string.isRequired,
            valor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            unidad: PropTypes.string,
            clasificacion: PropTypes.string,
        })
    ),
    fecha: PropTypes.string,
};

ValorCard.defaultProps = {
    valor: null,
    unidad: null,
    atenuada: false,
    clasificacion: null,
    filas: [],
    fecha: null,
};

export default ValorCard;
